import type { ReactNode } from "react";

// WhatsApp inline markup: *bold*, _italic_, ~strikethrough~ (never across a
// line break) and ```monospace``` (may span lines). Styles nest by recursion
// (*_bold italic_*). Unpaired markers are left as literal text, like WhatsApp.
const TOKEN = /```([\s\S]+?)```|\*([^*\n]+)\*|_([^_\n]+)_|~([^~\n]+)~/g;

const URL_REGEX = /https?:\/\/[^\s<>"]+/g;

// Plain runs get their URLs turned into links (reference linkify behavior).
function linkify(text: string, prefix: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const re = new RegExp(URL_REGEX.source, "g");
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const url = match[0];
    parts.push(
      <a
        key={`${prefix}u${match.index}`}
        href={url}
        target="_blank"
        rel="noreferrer"
        className="tw:break-all tw:text-[#027eb5] tw:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {url}
      </a>,
    );
    last = match.index + url.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function render(text: string, prefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const re = new RegExp(TOKEN.source, "g");
  let last = 0;
  let index = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last)
      nodes.push(
        ...linkify(text.slice(last, match.index), `${prefix}${index}`),
      );
    const key = `${prefix}${index++}`;
    if (match[1] !== undefined) {
      nodes.push(
        <code key={key} className="tw:font-mono tw:text-[0.9em]">
          {match[1]}
        </code>,
      );
    } else if (match[2] !== undefined) {
      nodes.push(
        <strong key={key} className="tw:font-bold">
          {render(match[2], `${key}.`)}
        </strong>,
      );
    } else if (match[3] !== undefined) {
      nodes.push(<em key={key}>{render(match[3], `${key}.`)}</em>);
    } else {
      nodes.push(<s key={key}>{render(match[4]!, `${key}.`)}</s>);
    }
    last = match.index + match[0].length;
  }
  if (last < text.length)
    nodes.push(...linkify(text.slice(last), `${prefix}${index}`));
  return nodes;
}

/**
 * Renders WhatsApp-style markup as rich text. Put it in a
 * `tw:whitespace-pre-wrap` container to keep line breaks (full previews); in a
 * plain container newlines collapse to spaces (list snippets).
 */
export function WhatsAppText({ text }: { text: string }) {
  return <>{render(text, "")}</>;
}
